import $ from 'jquery';

import { xmlescape } from '#public/commons-global';
import { useGenerateIds } from '#public_ts/composables';

import type { ClickTd } from './data-table';

export type DataTableButton<T> = {
    id?: string;
    title?: string;
    text?: string;
    icon?: string;
    button?: string;
    redner?: (this: DataTableButton<T>, data: T, prop: string, id: string) => string;
    exists?: (this: DataTableButton<T>, data: T) => boolean;
    hidden?: (this: DataTableButton<T>, data: T) => boolean;
    disabled?: (this: DataTableButton<T>, data: T) => boolean;
    handle?: (this: DataTableButton<T>, data: T, event: JQuery.TriggeredEvent) => void;
    item?: (this: DataTableButton<T>, data: T) => string;
};
export type DataTableButtons<T> = DataTableButton<T>[];

export function useDataTableButtons<T>(_buttons: DataTableButtons<T>) {
    const generateIds = useGenerateIds();
    const buttons = _buttons.map((b) => ({
        ...b,
        id: b.id || generateIds(),
    }));
    const ids = new Map(buttons.map((b) => [b.id || '', b]));
    const prop = 'data-table-custom-button';
    const itemProp = 'data-table-custom-button-item';

    function renderButttons(data: unknown, type: unknown, full: T) {
        const html = buttons.map((b) => {
            const id = b.id || '';
            if((b.exists !== undefined) && !b.exists(full)) {
                return '';
            }
            if(b.redner) {
                return b.redner(full, prop, id);
            }
            const hidden = b.hidden?.(full);
            const disabled = b.disabled?.(full);
            const item = b.item?.(full);
            return `<button ${prop}="${id}" type="button" class="btn ${b.button || 'btn-default'} btn-sm${hidden ? ' hidden' : ''}" \
title="${xmlescape(b.title || '')}"${disabled ? ' disabled="disabled"' : ''}${item ? ` ${itemProp}="${xmlescape(item)}"` : ''}>\
${b.icon ? `<span class="bi ${xmlescape(b.icon)}"></span>` : ''}\
${b.text ? xmlescape(b.text) : ''}\
</button>`;
        });
        return `<div style="white-space: nowrap; display: flex;" class="btn-group" role="group">${html.join('')}</div>`;
    }

    function handleButtons(params: ClickTd) {
        const button = params.target.closest(`[${prop}]`);
        if(!button.length) {
            return;
        }
        const id = button.attr(prop);
        const b = id && ids.get(id);
        if(!b || !b.handle) {
            return;
        }
        b.handle(params.data as T, params.event);
        params.event.stopPropagation();
    }

    function toggleButton(id: string, item: string, state: boolean) {
        const button = $(`[${prop}="${id}"][${itemProp}="${item}"]`);
        if(!button.length) {
            return;
        }
        button.toggleClass('hidden', !state);
    }

    function disableButton(id: string, item: string, state: boolean) {
        const button = $(`[${prop}="${id}"][${itemProp}="${item}"]`);
        if(!button.length) {
            return;
        }
        if(state) {
            button.attr('disabled', 'disabled');
        } else {
            button.removeAttr('disabled');
        }
    }

    return {
        renderButttons,
        handleButtons,
        toggleButton,
        disableButton,
    };
}
